import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AiOutlineFileExcel } from "react-icons/ai";
import { FiBookOpen, FiRefreshCw } from "react-icons/fi";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { Month } from "../../OtherData/Month";
import { errorAlert, infoAlert } from "../../Components/Alerts";
import { TaxPurchaseExcelForm } from "../../Print/TaxPurchaseExcelForm";
import { TaxPurchaseExcelFormNoVAT } from "../../Print/TaxPurchaseExcelFormNoVAT";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import { AddActionsButton } from "../../Components/AddElementsButton";
import DateInputDdMmYyyy from "../../Components/DateInputDdMmYyyy";
import ReusableAccountBooksGrid from "../../Components/ReusableAccountBooksGrid";
import CrudPageHeader from "../../Components/CrudPageHeader";
import "./TaxPurchaseBookPage.scss";
import "../../Style/__button.scss";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));
const toNumber = (value) => Number.parseFloat(value) || 0;

const emptyVatTotals = {
  BPT: 0,
  BDIIPT: 0,
  BIITJZ: 0,
  BIIMTJZ1: 0,
  importet18: 0,
  II18: 0,
  BV18: 0,
  BI18: 0,
  NotaKreditore18: 0,
  BK18: 0,
  RR18: 0,
  NK18: 0,
  TV18: 0,
  Importet8: 0,
  II8: 0,
  BV8: 0,
  BI8: 0,
  BF8: 0,
  NK8: 0,
  BK8: 0,
  RR8: 0,
  TVAT8: 0,
  TVAT8_18: 0,
};

const emptyNoVatTotals = {
  BMSH: 0,
  BPSH: 0,
  BI: 0,
  IM: 0,
  II: 0,
  BSHJV: 0,
  BT: 0,
};

const buildVatTotals = (items) => items.reduce((acc, item) => ({
  BPT: acc.BPT + toNumber(item.BlerjetPaTVSH),
  BDIIPT: acc.BDIIPT + toNumber(item.Blerjet_dhe_Imp_Inv_PaTVSH),
  BIITJZ: acc.BIITJZ + toNumber(item.Blerjet_dhe_Imp_Inv_me_TVSH_jo_TeZbritshme),
  BIIMTJZ1: acc.BIIMTJZ1 + toNumber(item.Blerjet_dhe_Imp_Inv_me_TVSH_jo_TeZbritshme1),
  importet18: acc.importet18 + toNumber(item.Importet18),
  II18: acc.II18 + toNumber(item.ImportetInvestive18),
  BV18: acc.BV18 + toNumber(item.BlerjetVendore18),
  BI18: acc.BI18 + toNumber(item.BlerjetInvestive18),
  NotaKreditore18: acc.NotaKreditore18 + toNumber(item.NotaKreditore18),
  BK18: acc.BK18 + toNumber(item.BorgjiKeq18),
  RR18: acc.RR18 + toNumber(item.Rregullimet18),
  NK18: acc.NK18 + toNumber(item.NgarkesaKundert18),
  TV18: acc.TV18 + toNumber(item.TotalTVSH18),
  Importet8: acc.Importet8 + toNumber(item.Importet8),
  II8: acc.II8 + toNumber(item.ImportetInvestive8),
  BV8: acc.BV8 + toNumber(item.BlerjetVendore8),
  BI8: acc.BI8 + toNumber(item.BlerjetInvestive8),
  BF8: acc.BF8 + toNumber(item.BlerjetNgaFermeret8),
  NK8: acc.NK8 + toNumber(item.NotaKreditore8),
  BK8: acc.BK8 + toNumber(item.BorgjiKeq8),
  RR8: acc.RR8 + toNumber(item.Rregullimet8),
  TVAT8: acc.TVAT8 + toNumber(item.TotalTVSH8),
  TVAT8_18: acc.TVAT8_18 + toNumber(item.Total_TVSH_8_dhe_18),
}), { ...emptyVatTotals });

const buildNoVatTotals = (items) => items.reduce((acc, item) => {
  const bms = toNumber(item.BlerjeVendoreMallrave);
  const bpsh = toNumber(item.BlerjeVendoreShpenzimeve);
  const bi = toNumber(item.BlerjeInvestive);
  const im = toNumber(item.ImporteMallra);
  const ii = toNumber(item.ImporteInvestive);
  const bshjv = toNumber(item.ImporteSherbimeve);

  return {
    BMSH: acc.BMSH + bms,
    BPSH: acc.BPSH + bpsh,
    BI: acc.BI + bi,
    IM: acc.IM + im,
    II: acc.II + ii,
    BSHJV: acc.BSHJV + bshjv,
    BT: acc.BT + bms + bpsh + bi + im + ii + bshjv,
  };
}, { ...emptyNoVatTotals });

function formatDateForDisplay(date) {
  if (!date) {
    return "";
  }

  if (date instanceof Date) {
    if (Number.isNaN(date.getTime())) {
      return "";
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  const raw = String(date).trim();
  if (!raw) {
    return "";
  }

  const dottedMatch = raw.match(/^(\d{2})[.](\d{2})[.](\d{4})$/);
  if (dottedMatch) {
    return raw;
  }

  const slashMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (slashMatch) {
    const [, day, month, year] = slashMatch;
    return `${day}.${month}.${year}`;
  }

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}.${month}.${year}`;
  }

  const parsedDate = new Date(raw);
  if (Number.isNaN(parsedDate.getTime())) {
    return raw;
  }

  const day = String(parsedDate.getDate()).padStart(2, "0");
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const year = parsedDate.getFullYear();
  return `${day}.${month}.${year}`;
}

const getPurchaseInvoiceNumber = (item, fiscalYear) => {
  const invoiceNr = item?.InvoiceNr || item?.NumriFatures;

  if (item?.ActionType === "IMP") {
    return item?.ImpInvoiceNr || invoiceNr || "-";
  }

  if (item?.ActionType === "POF") {
    const resolvedYear = item?.FiscalYear || fiscalYear;
    return `${item.ActionType}-${resolvedYear}-${invoiceNr || ""}`;
  }

  return invoiceNr || "-";
};

export default function TaxPurchaseBookPage() {
  const headers = authHeader();
  const { fiscalYear } = useContext(FiscalYearContext);

  const [data, setData] = useState([]);
  const [dataNoVAT, setDataNoVAT] = useState([]);
  const [month, setMonth] = useState(0);
  const [ngaData, setNgaData] = useState("");
  const [deriData, setDeriData] = useState("");
  const [minDate, setMinDate] = useState("");
  const [maxDate, setMaxDate] = useState("");
  const [companiesData, setCompaniesData] = useState([]);
  const [logoPath, setLogoPath] = useState("");
  const [vatDeclarant, setVatDeclarant] = useState(true);
  const [showMonthFilter, setShowMonthFilter] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingNoVat, setLoadingNoVat] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [quickFilter, setQuickFilter] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [totals, setTotals] = useState({ ...emptyVatTotals, ...emptyNoVatTotals });
  const [tm, setTm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const allTM = useMemo(() => ([
    { id: 1, title: "TM1", firstDate: `${fiscalYear}-01-01`, lastDate: `${fiscalYear}-03-31` },
    { id: 2, title: "TM2", firstDate: `${fiscalYear}-04-01`, lastDate: `${fiscalYear}-06-30` },
    { id: 3, title: "TM3", firstDate: `${fiscalYear}-07-01`, lastDate: `${fiscalYear}-09-30` },
    { id: 4, title: "TM4", firstDate: `${fiscalYear}-10-01`, lastDate: `${fiscalYear}-12-31` },
  ]), [fiscalYear]);

  useEffect(() => {
    setMinDate(`${fiscalYear}-01-01`);
    setMaxDate(`${fiscalYear}-12-31`);
  }, [fiscalYear]);

  useEffect(() => {
    const fetchCompaniesData = async () => {
      try {
        const response = await axios.get(ApiLink.API_URL + "Companies/getCompanyDetails", { headers });
        setCompaniesData(response.data || []);
        setLogoPath(response?.data?.[0]?.CompanyLogo ? `${ApiLink.PHOTO_URL}${response.data[0].CompanyLogo}` : "");
        setVatDeclarant(Boolean(response?.data?.[0]?.VATDeclarant));
      } catch (error) {
        errorAlert("Gabim në kërkesë:", error);
        if (error.response?.data?.errorMessage) {
          errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
        } else if (error.message) {
          errorAlert(`Gabim: ${error.message}`);
        } else {
          errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
        }
      }
    };

    fetchCompaniesData();
  }, [headers]);

  const openDateFilter = () => {
    setShowMonthFilter(false);
    setMonth(0);
    setData([]);
    setTotals((prev) => ({ ...prev, ...emptyVatTotals }));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const openMonthFilter = () => {
    setShowMonthFilter(true);
    setNgaData("");
    setDeriData("");
    setData([]);
    setTotals((prev) => ({ ...prev, ...emptyVatTotals }));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const buildPeriodParams = () => {
    const params = {};

    if (month && month >= 1 && month <= 12) {
      params.month = month;
      params.fiscalYear = fiscalYear;
    } else if (ngaData && deriData) {
      params.firstDate = ngaData;
      params.secondDate = deriData;
    } else {
      return null;
    }

    return params;
  };

  const fetchVatRows = async (page, pageSize) => {
    const periodParams = buildPeriodParams();
    if (!periodParams) {
      return { items: [], total: 0 };
    }

    const response = await axios.get(ApiLink.API_URL + "TaxReport/purchasebook-grid", {
      headers,
      params: {
        ...periodParams,
        page,
        pageSize,
        sort: JSON.stringify(sortModel),
        filter: JSON.stringify(filterModel.items || []),
        quickFilter: quickFilter.join(" "),
      },
    });

    const payload = response?.data;
    const items = Array.isArray(payload) ? payload : (payload?.items || payload?.data || []);
    const total = Array.isArray(payload)
      ? items.length
      : Number(payload?.total ?? payload?.totalRows ?? items.length);

    return { items, total };
  };

  const fetchData = async () => {
    if (!vatDeclarant) {
      return;
    }

    const periodParams = buildPeriodParams();
    if (!periodParams) {
      setData([]);
      setRowCount(0);
      setTotals((prev) => ({ ...prev, ...emptyVatTotals }));
      return;
    }

    setLoading(true);
    try {
      const { items, total } = await fetchVatRows(paginationModel.page, paginationModel.pageSize);
      setData(items);
      setRowCount(total);

      try {
        const { items: allItems } = await fetchVatRows(0, Math.max(total, items.length, 1));
        setTotals((prev) => ({ ...prev, ...buildVatTotals(allItems) }));
      } catch {
        setTotals((prev) => ({ ...prev, ...emptyVatTotals }));
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        infoAlert("Ju nuk keni qasje në këto të dhëna.");
      } else if (error.response?.data?.errorMessage) {
        errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDataNoVAT = async (first, second) => {
    if (!first || !second) {
      setDataNoVAT([]);
      setTotals((prev) => ({ ...prev, ...emptyNoVatTotals }));
      return;
    }

    setLoadingNoVat(true);
    try {
      const response = await axios.get(ApiLink.API_URL + "TaxReport/purchasebooknovat", {
        headers,
        params: {
          firstDate: first,
          secondDate: second,
        },
      });

      const items = Array.isArray(response?.data) ? response.data : [];
      setDataNoVAT(items);
      setTotals((prev) => ({ ...prev, ...buildNoVatTotals(items) }));
    } catch (error) {
      errorAlert("Gabim në kërkesë:", error);
      if (error.response?.data?.errorMessage) {
        errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
      }
    } finally {
      setLoadingNoVat(false);
    }
  };

  useEffect(() => {
    if (!vatDeclarant) {
      return;
    }

    fetchData();
  }, [month, fiscalYear, ngaData, deriData, paginationModel, sortModel, filterModel, quickFilter, vatDeclarant]);

  useEffect(() => {
    if (vatDeclarant || !startDate || !endDate) {
      return;
    }

    fetchDataNoVAT(startDate, endDate);
  }, [vatDeclarant, startDate, endDate]);

  const handleTmChange = (event) => {
    const selectedId = Number(event.target.value);
    setTm(selectedId);
    const selectedTM = allTM.find((item) => item.id === selectedId);
    if (!selectedTM) {
      setStartDate("");
      setEndDate("");
      setDataNoVAT([]);
      setTotals((prev) => ({ ...prev, ...emptyNoVatTotals }));
      return;
    }

    setStartDate(selectedTM.firstDate);
    setEndDate(selectedTM.lastDate);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns = useMemo(() => ([
    { field: "rowNumber", headerName: "#", minWidth: 70, width: 80 },
    { field: "date", headerName: "Data Fatures", minWidth: 130, width: 140 },
    { field: "nr", headerName: "Numri Faturës", minWidth: 180, width: 220 },
    { field: "blersi", headerName: "Emri i shitësit", minWidth: 220, width: 250 },
    { field: "nui", headerName: "Numri Identifikues", minWidth: 180, width: 210 },
    { field: "vat", headerName: "Numri i TVSH-së", minWidth: 170, width: 190 },
    { field: "BPT", headerName: "Blerjet Pa TVSH", minWidth: 150, width: 170, valueFormatter: (value) => formatCurrency(value) },
    { field: "BDIIPT", headerName: "Blerjet dhe Imp Inv Pa TVSH", minWidth: 180, width: 220, valueFormatter: (value) => formatCurrency(value) },
    { field: "BIITJZ", headerName: "Blerjet dhe Imp Inv me TVSH jo Te Zbritshme", minWidth: 220, width: 260, valueFormatter: (value) => formatCurrency(value) },
    { field: "BIIMTJZ1", headerName: "Blerjet dhe Imp Inv me TVSH jo Te Zbritshme 1", minWidth: 240, width: 280, valueFormatter: (value) => formatCurrency(value) },
    { field: "importet18", headerName: "Importet 18", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
    { field: "II18", headerName: "Importet Investive 18", minWidth: 160, width: 190, valueFormatter: (value) => formatCurrency(value) },
    { field: "BV18", headerName: "Blerjet Vendore 18", minWidth: 150, width: 175, valueFormatter: (value) => formatCurrency(value) },
    { field: "BI18", headerName: "Blerjet Investive 18", minWidth: 150, width: 175, valueFormatter: (value) => formatCurrency(value) },
    { field: "NotaKreditore18", headerName: "Nota Kreditore 18", minWidth: 150, width: 175, valueFormatter: (value) => formatCurrency(value) },
    { field: "BK18", headerName: "Borgji Keq 18", minWidth: 140, width: 160, valueFormatter: (value) => formatCurrency(value) },
    { field: "RR18", headerName: "Rregullimet 18", minWidth: 140, width: 160, valueFormatter: (value) => formatCurrency(value) },
    { field: "NK18", headerName: "Ngarkesa Kundert 18", minWidth: 160, width: 190, valueFormatter: (value) => formatCurrency(value) },
    { field: "TV18", headerName: "Total TVSH 18", minWidth: 140, width: 160, valueFormatter: (value) => formatCurrency(value) },
    { field: "Importet8", headerName: "Importet 8", minWidth: 130, width: 150, valueFormatter: (value) => formatCurrency(value) },
    { field: "II8", headerName: "Importet Investive 8", minWidth: 160, width: 190, valueFormatter: (value) => formatCurrency(value) },
    { field: "BV8", headerName: "Blerjet Vendore 8", minWidth: 150, width: 175, valueFormatter: (value) => formatCurrency(value) },
    { field: "BI8", headerName: "Blerjet Investive 8", minWidth: 150, width: 175, valueFormatter: (value) => formatCurrency(value) },
    { field: "BF8", headerName: "Blerjet Nga Fermeret 8", minWidth: 170, width: 200, valueFormatter: (value) => formatCurrency(value) },
    { field: "NK8", headerName: "Nota Kreditore 8", minWidth: 150, width: 175, valueFormatter: (value) => formatCurrency(value) },
    { field: "BK8", headerName: "Borgji Keq 8", minWidth: 140, width: 160, valueFormatter: (value) => formatCurrency(value) },
    { field: "RR8", headerName: "Rregullimet 8", minWidth: 140, width: 160, valueFormatter: (value) => formatCurrency(value) },
    { field: "TVAT8", headerName: "Total TVSH 8", minWidth: 140, width: 160, valueFormatter: (value) => formatCurrency(value) },
    { field: "TVAT8_18", headerName: "Total TVSH 8 dhe 18", minWidth: 170, width: 200, valueFormatter: (value) => formatCurrency(value) },
  ]), []);

  const columnsNoVAT = useMemo(() => ([
    { field: "rowNumber", headerName: "#", minWidth: 70, width: 80 },
    { field: "date", headerName: "Data Fatures", minWidth: 130, width: 140 },
    { field: "nr", headerName: "Numri Fatures", minWidth: 180, width: 220 },
    { field: "invType", headerName: "Lloji transaksionit", minWidth: 160, width: 190 },
    { field: "type", headerName: "Import/Export", minWidth: 140, width: 170 },
    { field: "blersi", headerName: "Emri Bleresit", minWidth: 220, width: 250 },
    { field: "nui", headerName: "Numri Identifikues", minWidth: 180, width: 210 },
    { field: "BMSH", headerName: "Blerje e mallrave / shërbimeve", minWidth: 170, width: 210, valueFormatter: (value) => formatCurrency(value) },
    { field: "BPSH", headerName: "Blerje për shpenzime", minWidth: 160, width: 190, valueFormatter: (value) => formatCurrency(value) },
    { field: "BI", headerName: "Blerje investive", minWidth: 150, width: 175, valueFormatter: (value) => formatCurrency(value) },
    { field: "IM", headerName: "Importe të mallrave", minWidth: 150, width: 175, valueFormatter: (value) => formatCurrency(value) },
    { field: "II", headerName: "Importe investive", minWidth: 150, width: 175, valueFormatter: (value) => formatCurrency(value) },
    { field: "BSHJV", headerName: "Blerje të shërbimeve jashtë vendit", minWidth: 190, width: 230, valueFormatter: (value) => formatCurrency(value) },
    { field: "BT", headerName: "Blerjet totale", minWidth: 140, width: 170, valueFormatter: (value) => formatCurrency(value) },
  ]), []);

  const rows = useMemo(() => (
    data.map((item, index) => ({
      id: item.Nr || `${item.NumriFatures || "purchase"}-${index}`,
      rowNumber: item.Nr || (paginationModel.page * paginationModel.pageSize + index + 1),
      date: formatDateForDisplay(item.DataFatures),
      nr: getPurchaseInvoiceNumber(item, fiscalYear),
      blersi: item.EmriShitesit || "",
      nui: item.IdentificationNumber || "",
      vat: item.VATNumber || "",
      BPT: item.BlerjetPaTVSH || 0,
      BDIIPT: item.Blerjet_dhe_Imp_Inv_PaTVSH || 0,
      BIITJZ: item.Blerjet_dhe_Imp_Inv_me_TVSH_jo_TeZbritshme || 0,
      BIIMTJZ1: item.Blerjet_dhe_Imp_Inv_me_TVSH_jo_TeZbritshme1 || 0,
      importet18: item.Importet18 || 0,
      II18: item.ImportetInvestive18 || 0,
      BV18: item.BlerjetVendore18 || 0,
      BI18: item.BlerjetInvestive18 || 0,
      NotaKreditore18: item.NotaKreditore18 || 0,
      BK18: item.BorgjiKeq18 || 0,
      RR18: item.Rregullimet18 || 0,
      NK18: item.NgarkesaKundert18 || 0,
      TV18: item.TotalTVSH18 || 0,
      Importet8: item.Importet8 || 0,
      II8: item.ImportetInvestive8 || 0,
      BV8: item.BlerjetVendore8 || 0,
      BI8: item.BlerjetInvestive8 || 0,
      BF8: item.BlerjetNgaFermeret8 || 0,
      NK8: item.NotaKreditore8 || 0,
      BK8: item.BorgjiKeq8 || 0,
      RR8: item.Rregullimet8 || 0,
      TVAT8: item.TotalTVSH8 || 0,
      TVAT8_18: item.Total_TVSH_8_dhe_18 || 0,
      rowInfo: item,
    }))
  ), [data, paginationModel.page, paginationModel.pageSize, fiscalYear]);

  const rowsNoVATAll = useMemo(() => (
    dataNoVAT.map((item, index) => {
      const total = toNumber(item.BlerjeVendoreMallrave)
        + toNumber(item.BlerjeVendoreShpenzimeve)
        + toNumber(item.BlerjeInvestive)
        + toNumber(item.ImporteMallra)
        + toNumber(item.ImporteInvestive)
        + toNumber(item.ImporteSherbimeve);

      return {
        id: item.Nr || `${item.InvoiceNr || "purchase-novat"}-${index}`,
        rowNumber: index + 1,
        date: formatDateForDisplay(item.Date),
        nr: getPurchaseInvoiceNumber(item, fiscalYear),
        invType: item.ActionType || "",
        type: item.TransactionType || "",
        blersi: item.AccountName || "",
        nui: item.IdentificationNumber || "",
        BMSH: item.BlerjeVendoreMallrave || 0,
        BPSH: item.BlerjeVendoreShpenzimeve || 0,
        BI: item.BlerjeInvestive || 0,
        IM: item.ImporteMallra || 0,
        II: item.ImporteInvestive || 0,
        BSHJV: item.ImporteSherbimeve || 0,
        BT: total,
        rowInfo: item,
      };
    })
  ), [dataNoVAT, fiscalYear]);

  const rowsNoVAT = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    const end = start + paginationModel.pageSize;
    return rowsNoVATAll.slice(start, end);
  }, [rowsNoVATAll, paginationModel]);

  const fetchAllRowsForExport = async () => {
    if (!vatDeclarant) {
      return dataNoVAT;
    }

    const periodParams = buildPeriodParams();
    if (!periodParams) {
      return [];
    }

    const totalRowsToExport = Math.max(Number(rowCount) || 0, 1);
    const response = await axios.get(ApiLink.API_URL + "TaxReport/purchasebook-grid", {
      headers,
      params: {
        ...periodParams,
        page: 0,
        pageSize: totalRowsToExport,
        sort: JSON.stringify(sortModel),
        filter: JSON.stringify(filterModel.items || []),
        quickFilter: quickFilter.join(" "),
      },
    });

    const payload = response?.data;
    if (Array.isArray(payload)) {
      return payload;
    }

    return payload?.items || payload?.data || [];
  };

  const handleExportExcel = async () => {
    try {
      if (vatDeclarant) {
        const exportRows = await fetchAllRowsForExport();
        if (!exportRows.length) {
          infoAlert("Nuk ka të dhëna për eksport.");
          return;
        }

        const workbook = TaxPurchaseExcelForm(exportRows, fiscalYear);
        const fileName = month === 0
          ? `LB_Periudha nga_${formatDateForDisplay(ngaData)}_Deri_${formatDateForDisplay(deriData)}.xlsx`
          : `LB_${month}_${fiscalYear}.xlsx`;

        workbook.xlsx.writeBuffer().then((buffer) => {
          const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName;
          a.click();
          URL.revokeObjectURL(url);
        });
        return;
      }

      if (!dataNoVAT.length) {
        infoAlert("Nuk ka të dhëna për eksport.");
        return;
      }

      const workbook = TaxPurchaseExcelFormNoVAT(dataNoVAT, fiscalYear);
      const fileName = `LB_TM${tm || ""}_${fiscalYear}.xlsx`;
      workbook.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      });
    } catch (error) {
      if (error.response?.data?.errorMessage) {
        errorAlert(`Gabim në eksport: ${error.response.data.errorMessage}`);
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert("Gabim i panjohur gjatë eksportit.");
      }
    }
  };

  const filterToolbar = vatDeclarant ? (
    <>
      <button
        type="button"
        className="RSDG__toolButton TPBP__toolbarSwitch"
        onClick={() => {
          if (showMonthFilter) {
            openDateFilter();
          } else {
            openMonthFilter();
          }
        }}
        aria-label="Ndrysho mënyrën e filtrimit"
        title={`Ndrysho filtrin: ${showMonthFilter ? "Muaji" : "Datat"}`}
      >
        <span className="RSDG__toolButtonContent">
          <FiRefreshCw size={14} className={`TPBP__toolbarSwitchIcon${showMonthFilter ? "" : " TPBP__toolbarSwitchIcon--rotated"}`} />
          <span>{showMonthFilter ? "Muaji" : "Datat"}</span>
        </span>
      </button>

      {showMonthFilter ? (
        <TextField
          className="TPBP__toolbarField TPBP__toolbarField--month"
          select
          size="small"
          label="Muaji"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          required
        >
          {Month.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {fiscalYear} {item.month}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <>
          <div className="TPBP__toolbarDateWrap TPBP__toolbarDateWrap--date">
            <DateInputDdMmYyyy label="Nga" value={ngaData} minDate={minDate} maxDate={maxDate} onChange={setNgaData} fullWidth={false} />
          </div>
          <div className="TPBP__toolbarDateWrap TPBP__toolbarDateWrap--date">
            <DateInputDdMmYyyy label="Deri" value={deriData} minDate={minDate} maxDate={maxDate} onChange={setDeriData} fullWidth={false} />
          </div>
        </>
      )}
    </>
  ) : (
    <TextField
      className="TPBP__toolbarField TPBP__toolbarField--month"
      select
      size="small"
      label="Përzgjedh 3-Mujorin"
      value={tm}
      onChange={(e) => {
        handleTmChange(e);
        setTm(e.target.value);
      }}
      required
    >
      {allTM.map((item) => (
        <MenuItem key={item.id} value={item.id}>
          {item.title}
        </MenuItem>
      ))}
    </TextField>
  );

  const hasSelectedPeriod = vatDeclarant
    ? ((month && month >= 1 && month <= 12) || (ngaData && deriData))
    : Boolean(tm);

  const emptyGridMessage = hasSelectedPeriod
    ? "Nuk ka te dhena."
    : (vatDeclarant
      ? "Ju lutem zgjidhni periudhen (muajin ose datat)."
      : "Ju lutem zgjidhni 3-mujorin.");

  const summaryFooter = vatDeclarant
    ? {
      labelField: "vat",
      label: "Totali",
      values: {
        BPT: formatCurrency(totals.BPT),
        BDIIPT: formatCurrency(totals.BDIIPT),
        BIITJZ: formatCurrency(totals.BIITJZ),
        BIIMTJZ1: formatCurrency(totals.BIIMTJZ1),
        importet18: formatCurrency(totals.importet18),
        II18: formatCurrency(totals.II18),
        BV18: formatCurrency(totals.BV18),
        BI18: formatCurrency(totals.BI18),
        NotaKreditore18: formatCurrency(totals.NotaKreditore18),
        BK18: formatCurrency(totals.BK18),
        RR18: formatCurrency(totals.RR18),
        NK18: formatCurrency(totals.NK18),
        TV18: formatCurrency(totals.TV18),
        Importet8: formatCurrency(totals.Importet8),
        II8: formatCurrency(totals.II8),
        BV8: formatCurrency(totals.BV8),
        BI8: formatCurrency(totals.BI8),
        BF8: formatCurrency(totals.BF8),
        NK8: formatCurrency(totals.NK8),
        BK8: formatCurrency(totals.BK8),
        RR8: formatCurrency(totals.RR8),
        TVAT8: formatCurrency(totals.TVAT8),
        TVAT8_18: formatCurrency(totals.TVAT8_18),
      },
    }
    : {
      labelField: "nui",
      label: "Totali",
      values: {
        BMSH: formatCurrency(totals.BMSH),
        BPSH: formatCurrency(totals.BPSH),
        BI: formatCurrency(totals.BI),
        IM: formatCurrency(totals.IM),
        II: formatCurrency(totals.II),
        BSHJV: formatCurrency(totals.BSHJV),
        BT: formatCurrency(totals.BT),
      },
    };

  return (
    <div className="Crud__Container TPBP__page">
      <div className="TPBP__content">
        <CrudPageHeader
          icon={FiBookOpen}
          title="Libri i Blerjeve per ATK"
          description="Ky format i librit te blerjeve eshte i pershtatur per ngarkim ne sistemin e ATK-se."
          className="TPBP__header"
        />

        <div className="TPBP__reportGrid">
          <ReusableAccountBooksGrid
            columns={vatDeclarant ? columns : columnsNoVAT}
            rows={vatDeclarant ? rows : rowsNoVAT}
            loading={vatDeclarant ? loading : loadingNoVat}
            rowCount={vatDeclarant ? rowCount : rowsNoVATAll.length}
            height={650}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            sortModel={sortModel}
            setSortModel={setSortModel}
            filterModel={filterModel}
            setFilterModel={setFilterModel}
            quickFilter={quickFilter}
            setQuickFilter={setQuickFilter}
            showQuickSearch={false}
            showExportButton={false}
            toolbarActions={(
              <AddActionsButton
                text="XLSX"
                onClick={handleExportExcel}
                icon={<AiOutlineFileExcel />}
                className="RSDG__toolButton TPBP__gridActionBtn--xlsx"
              />
            )}
            toolbarRightActions={filterToolbar}
            showViewAction={false}
            showEditAction={false}
            showDeleteAction={false}
            emptyMessage={emptyGridMessage}
            summaryFooter={summaryFooter}
          />
        </div>
      </div>
    </div>
  );
}
